import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '@/lib/utils';
import { Loader2, Plus } from 'lucide-react';

const buttonVariants = cva(
  `
  inline-flex items-center justify-center gap-2
  whitespace-nowrap rounded-md text-sm font-medium
  transition-colors
  focus-visible:outline-none
  focus-visible:ring-2
  focus-visible:ring-blue-500
  focus-visible:ring-offset-2
  focus-visible:ring-offset-white
  disabled:pointer-events-none disabled:opacity-50
  [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0

  dark:focus-visible:ring-blue-400
  dark:focus-visible:ring-offset-slate-900
  `,
  {
    variants: {
      variant: {
        default: `
          bg-blue-600 text-white
          hover:bg-blue-700
          dark:bg-blue-500 dark:hover:bg-blue-600
        `,

        destructive: `
          bg-red-600 text-white
          hover:bg-red-700
          dark:bg-red-500 dark:hover:bg-red-600
        `,

        secondary: `
          bg-slate-100 text-slate-800
          hover:bg-slate-200
          dark:bg-slate-800 dark:text-slate-200
          dark:hover:bg-slate-700
        `,

        outline: `
          border border-slate-200
          bg-transparent text-slate-700
          hover:bg-slate-100
          dark:border-slate-700 dark:text-slate-200
          dark:hover:bg-slate-800
        `,

        ghost: `
          text-slate-700
          hover:bg-slate-100
          dark:text-slate-200
          dark:hover:bg-slate-800
        `,

        link: `
          text-indigo-600 underline-offset-4 hover:underline
          dark:text-indigo-400
        `,

        tertiary: `
          bg-blue-50 text-blue-700
          hover:bg-blue-100
          dark:bg-blue-900/30 dark:text-blue-300
          dark:hover:bg-blue-900/50
        `,

        icon: `
          bg-slate-100 text-slate-700
          hover:bg-slate-200
          dark:bg-slate-800 dark:text-slate-200
          dark:hover:bg-slate-700
        `,

        dashed: `
          border border-dashed border-slate-300
          text-slate-700
          hover:bg-slate-100
          dark:border-slate-600 dark:text-slate-200
          dark:hover:bg-slate-800
        `,
      },

      size: {
        default: 'h-8 px-2.5 py-1.5',
        sm: 'h-6 rounded-sm px-2',
        lg: 'h-11 rounded-md px-8',
        icon: 'h-10 w-10',
        auto: 'h-full px-1',
      },
    },

    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);


export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(
          'bg-background-card',
          buttonVariants({ variant, size, className }),
        )}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = 'Button';

export const ButtonLoading = React.forwardRef<
  HTMLButtonElement,
  ButtonProps & { loading?: boolean }
>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      children,
      loading = false,
      disabled,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
        disabled={loading || disabled}
      >
        {loading && <Loader2 className="animate-spin" />}
        {children}
      </Comp>
    );
  },
);

ButtonLoading.displayName = 'ButtonLoading';

export { Button, buttonVariants };

export const BlockButton = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ children, className, ...props }, ref) => {
    return (
      <Button
        variant={'outline'}
        ref={ref}
        className={cn('w-full border-dashed border-input-border', className)}
        {...props}
      >
        <Plus /> {children}
      </Button>
    );
  },
);
